import { useContext, useRef, useState } from "react";
import DashboardCardHeader from "../DashboardCardHeader";
import { Box, CircularProgress, Container } from "@mui/material";
import { RopChart } from "../../../../main/components/RopChart";
import { MainContext } from "../../../../main/context/MainContextProvider";
import {
  getHourAndMinutes,
  getYearDay,
} from "../../../../main/themes/defaultLayout";
import { DashboardContext } from "../../context/DashboardContexProvider";
import {
  BaseErrorCard,
  BaseUsinaValue,
} from "../../models/dados-indicadores.model";
import { EmptyTable } from "../../../clients/components/EmptyState";
import { CardTypesEnum } from "../../types";
import { getCardIsLoading } from "../TextCardContent";

export type ProductivityProps = {
  cardId?: string;
  title?: string;
  lastUpdate: Date;
  cardType: number;
  data: {
    generatedEnergy?: BaseUsinaValue[];
    predictedEnergy?: BaseUsinaValue[];
    irradiance?: BaseUsinaValue[];
    predictedIrradianceMonths?: BaseUsinaValue[];
    expectedIrradianciaAtDay?: number;
    expectedIrradianciaAtHour?: number;
    expectedEnergyAtDay?: number;
    expectedEnergyAtHour?: number;
    expectedIrradianciaNormalizedAtHour: BaseUsinaValue[];
    expectedEnergyNormalizedAtHour: BaseUsinaValue[];
    cardError: BaseErrorCard;
  };
};

export default function ProductivityChart(props: ProductivityProps) {
  const elementRef = useRef(null);
  const { data, title, lastUpdate, cardType } = props;
  const [isMouseIn, setIsMouseIn] = useState<boolean>(false);
  const { isMobile } = useContext(MainContext);
  const {
    cardsDisplayableColorProps,
    setCardsDisplayableColorProps,
    cardIsLoading,
    requestParamCards,
  } = useContext(DashboardContext);

  const [energiaGeradaColor, setEnergiaGeradaColor] = useState<string>(
    cardsDisplayableColorProps.produtividade.energiaGeradaColor ?? "#EE7D00"
  );
  const [energiaPrevistaColor, setEnergiaPrevistaColor] = useState<string>(
    cardsDisplayableColorProps.produtividade.energiaPrevistaColor ?? "#FADE0A"
  );
  const [irradianciaColor, setIrradianciaColor] = useState<string>(
    cardsDisplayableColorProps.produtividade.irradianciaColor ?? "#FAB900"
  );
  const [irradianciaPrevistaColor, setIrradianciaPrevistaColor] =
    useState<string>(
      cardsDisplayableColorProps.produtividade.irradianciaPrevistaColor ??
        "#6D0DB8"
    );

  const dates = data.generatedEnergy
    ? data.generatedEnergy.map((item) => getHourAndMinutes(item.dateTime ?? ""))
    : [];
  const collectDay =
    data.generatedEnergy && data.generatedEnergy.length > 0
      ? getYearDay(data.generatedEnergy[0].dateTime ?? "")
      : "";
  const generatedEnergy = data.generatedEnergy
    ? data.generatedEnergy.map((item) => Number(item.value?.toFixed(2)))
    : [];

  const filterIrradianciaNormalized =
    data.expectedIrradianciaNormalizedAtHour?.filter((item) =>
      data.generatedEnergy?.some((e) => e.dateTime === item.dateTime)
    );

  const filterEnergyNormalized = data.expectedEnergyNormalizedAtHour?.filter(
    (item) => data.generatedEnergy?.some((e) => e.dateTime === item.dateTime)
  );

  const irradiance = data.irradiance
    ? data.irradiance.map((item) => Number(item.value?.toFixed(2)))
    : [];

  const expectedEnergyAtHour = filterEnergyNormalized
    ? filterEnergyNormalized.map((item) => Number(item.value?.toFixed(2)))
    : [];
  const predictedIrradiance = filterIrradianciaNormalized
    ? filterIrradianciaNormalized.map((item) => Number(item.value?.toFixed(2)))
    : [];

  const option = {
    title: {
      text: collectDay,
      textStyle: {
        fontSize: 14,
        fontWeight: "bold",
      },
    },
    backgroundColor: "transparent",

    tooltip: {
      trigger: "axis",
      confine: true,
    },
    legend: {
      left: "right",
      data: [
        "Energia Gerada (MWh)",
        "Energia Prevista (MWh)",
        "Irradiância (W/m²)",
        "Irradiância Prevista (W/m²)",
      ],
    },
    grid: {
      top: isMobile ? 180 : 120,
      // containLabel: true,
    },
    xAxis: [
      {
        data: dates,
        axisPointer: {
          type: "shadow",
        },
      },
    ],
    yAxis: [
      {
        name: "MWh",
        type: "value",
      },
      {
        name: "W/m²",
        type: "value",
        top: "5%",
      },
    ],
    series: [
      {
        name: "Irradiância Prevista (W/m²)",
        type: "line",
        data: predictedIrradiance,
        yAxisIndex: 1,
        itemStyle: {
          color: irradianciaPrevistaColor,
        },
      },
      {
        name: "Irradiância (W/m²)",
        type: "line",
        data: irradiance,
        yAxisIndex: 1,
        itemStyle: {
          color: irradianciaColor,
        },
      },
      {
        name: "Energia Prevista (MWh)",
        type: "bar",
        data: expectedEnergyAtHour,
        yAxisIndex: 0,
        itemStyle: {
          color: energiaPrevistaColor,
        },
      },
      {
        name: "Energia Gerada (MWh)",
        type: "bar",
        data: generatedEnergy,
        areaStyle: {},
        itemStyle: {
          color: energiaGeradaColor,
        },
        lineStyle: {
          width: 0,
        },
        emphasis: {
          focus: "series",
        },
        yAxisIndex: 0,
      },
    ],
  };

  const headerTitle = title ?? "Produtividade";
  const timeout = 120;
  const now = new Date();
  const delay = lastUpdate ? (now.getTime() - lastUpdate.getTime()) / 1000 : 0;
  const hasDelay = delay > timeout;

  const [openModal, setopenModal] = useState<boolean>(false);

  const handleConfirmModal = () => {
    setCardsDisplayableColorProps({
      ...cardsDisplayableColorProps,
      produtividade: {
        energiaGeradaColor: energiaGeradaColor,
        energiaPrevistaColor: energiaPrevistaColor,
        irradianciaColor: irradianciaColor,
        irradianciaPrevistaColor: irradianciaPrevistaColor,
      },
    });
    setopenModal(false);
  };

  const hasData =
    predictedIrradiance.length > 0 ||
    irradiance.length > 0 ||
    expectedEnergyAtHour.length > 0 ||
    generatedEnergy.length > 0;

  return (
    <>
      <div
        style={{
          height: "100%",
          width: "100%",
          display: "flex",
          flexDirection: "column",
        }}
        onMouseOver={() => setIsMouseIn(true)}
        onMouseLeave={() => setIsMouseIn(false)}
      >
        <DashboardCardHeader
          cardError={data.cardError}
          onConfirmColorsModal={handleConfirmModal}
          hasDelay={hasDelay}
          lastUpdate={lastUpdate}
          cardId={props.cardId}
          elementRef={elementRef}
          isMouseIn={isMouseIn}
          title={headerTitle}
          titleFontSize={21}
          openColorsModal={openModal}
          setOpenColorsModal={setopenModal}
          colorsOptions={[
            {
              title: "Energia Gerada",
              value: energiaGeradaColor,
              setValue: setEnergiaGeradaColor,
            },
            {
              title: "Energia Prevista",
              value: energiaPrevistaColor,
              setValue: setEnergiaPrevistaColor,
            },
            {
              title: "Irradiancia",
              value: irradianciaColor,
              setValue: setIrradianciaColor,
            },
            {
              title: "Irradiaância Prevista",
              value: irradianciaPrevistaColor,
              setValue: setIrradianciaPrevistaColor,
            },
          ]}
          colorButton
          cardType={CardTypesEnum.Produtividade}
          hasFilter
        />
        {!getCardIsLoading(cardType, cardIsLoading, requestParamCards) ? (
          hasData ? (
            <Container
              ref={elementRef}
              sx={{
                display: "flex",
                flexDirection: "column",
                flexGrow: 1,
                height: "90%",
              }}
            >
              <RopChart option={option} />
            </Container>
          ) : (
            <Box
              sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <EmptyTable title="Nenhum dado encontrado!" />
            </Box>
          )
        ) : (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              justifyContent: "center",
              alignItems: "center",
              flexGrow: 1,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <CircularProgress />
          </Box>
        )}
      </div>
    </>
  );
}
